import { html, nothing, type TemplateResult } from "lit";
import type { CompactionStatus } from "../app-tool-stream.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import { buildLiveRunStepsWithSticky, type LiveRunStep } from "./chat-live-run.ts";

/**
 * chat-composer-live-step.ts — JAISIU-composer-live-step-indicator
 *
 * Compact, single-step mirror of the pinned-top live-run bubble for the
 * chat composer footer. While the model is in-flight, the operator sees:
 *
 *   <span class="chat-compose-live-pill chat-compose-live-pill--thinking">
 *     <span class="chat-compose-live-pill__dot" aria-hidden="true"></span>
 *     <span class="chat-compose-live-pill__label">Thinking…</span>
 *   </span>
 *
 * Returns `nothing` when no live step exists, so the call site can fall
 * back to `renderChatSyncPill` (Awaiting reply / Syncing / Disconnected)
 * unchanged. This keeps the staleness surface decoupled from the
 * mid-run activity surface — see chat-sync-pill.ts comments.
 *
 * Reuses `buildLiveRunStepsWithSticky` so the sticky-hint latching the
 * pinned bubble relies on carries over to the composer without
 * reinventing it: a transient null hint in the gateway event stream
 * does not blank the pill.
 *
 * Design notes:
 * - Only the topmost active step is rendered. The pinned bubble already
 *   shows the full list; the composer footer needs one label, not five.
 * - Dot colour is variant-driven (--thinking / --tool / --reasoning /
 *   --hint / --compaction / --sub) so the operator can read the phase
 *   without reading the label.
 * - The pill animates with a dual-layer ripple (see fleet-theme.css) so
 *   the dot reads as a heartbeat, not a metronome.
 *
 * Note on `class=${className}` — the variant suffix is built as a single
 * string before interpolation. Lit splits static + interpolated parts of
 * an attribute across separate template slots, so writing
 * `class="chat-compose-live-pill chat-compose-live-pill--${variant}"`
 * would store the suffix as its own template value and make it awkward
 * to assert against (see chat-composer-live-step.node.test.ts).
 */

export type ComposerLiveStepInput = {
  /** True while the chat run is in flight (matches `liveRunStep` activation). */
  runActive: boolean;
  toolMessageCount: number;
  hasActiveTool: boolean;
  streamText: string;
  reasoningStreamText?: string;
  liveRunHint?: string | null;
  /** Sticky fallback — latched across renders, mirrors chat-live-run. */
  stickyLiveRunHint?: string | null;
  compactionStatus: CompactionStatus | null;
  subagents: ChatSubagentCard[];
  activeFileToolPath?: string | null;
  /**
   * JAISIU-3752 — one-line summary of the model's most recent thinking,
   * derived by `resolveLiveThinkingSummary`. Sourced by the caller
   * (chat.ts renderComposerStatusSlot) from the same fields the
   * Inspector activity panel uses. The composer pill surfaces the
   * summary as the primary label so the footer stays in lockstep with
   * the pinned bubble.
   */
  thinkingSummary?: string | null;
  /**
   * Sticky fallback for `thinkingSummary`. Same latching contract as
   * `stickyLiveRunHint`: first non-empty summary sticks; only a
   * *different* non-empty summary replaces it; the null → null
   * cycle is dropped.
   */
  stickyThinkingSummary?: string | null;
  /**
   * JAISIU-chat-switch-reload — number of parked messages waiting on
   * the in-flight run. Rendered as a `queued` pill when the run has
   * closed but the queue is still non-empty (the run closed between
   * tab switches; the depth is the depth from the per-session live
   * snapshot, not the global counter). Zero / omitted suppresses the
   * pill — the standard sync / idle render takes over.
   */
  queuedCount?: number;
};

/** Topmost active step in the same order the pinned bubble emits them. */
export function pickTopmostLiveStep(steps: LiveRunStep[]): LiveRunStep | null {
  for (const step of steps) {
    if (step.state === "active") {
      return step;
    }
  }
  return null;
}

/**
 * Map a `LiveRunStep.id` to a CSS variant suffix used by the dot colour
 * and the ripple keyframe timing.
 *
 *   compaction  → cyan-violet pulse
 *   sub         → cyan pulse (one per active subagent step)
 *   live-hint   → cyan pulse (gateway-supplied label)
 *   file-tool   → amber pulse (mutating filesystem)
 *   tool-active → blue pulse (tools in flight)
 *   reasoning   → violet pulse
 *   generating  → cyan pulse
 *   thinking    → cyan pulse (default)
 *   thinking-summary → cyan pulse (JAISIU-3752 — model message, same
 *                      family as `thinking` so the operator reads the
 *                      pill as 'the model is still working' even when
 *                      the label is now a real sentence instead of
 *                      'Thinking…').
 *   queued      → slow amber pulse (no run active, parked messages waiting)
 */
export function stepVariant(stepId: string): string {
  if (stepId === "compaction") {
    return "compaction";
  }
  if (stepId.startsWith("sub:")) {
    return "sub";
  }
  if (stepId === "live-hint") {
    return "hint";
  }
  if (stepId === "file-tool") {
    return "file";
  }
  if (stepId === "tool-active") {
    return "tool";
  }
  if (stepId === "reasoning") {
    return "reasoning";
  }
  if (stepId === "thinking-summary") {
    return "thinking";
  }
  if (stepId === "queued") {
    return "queued";
  }
  // generating + thinking (and any future id) fall through to default.
  return "thinking";
}

/**
 * Pure: returns the topmost live step (or `null`) plus the sticky hint
 * the caller should pass on the next render. Splitting the data fetch
 * from the renderer makes this trivially testable.
 *
 * JAISIU-chat-switch-reload — the `queued` step is surfaced when the
 * run has closed but the queue depth is non-zero. This closes the
 * gap where a tab-switch back to a session whose run finished between
 * switches left the composer with no pill at all — the operator saw
 * the big `chat-queue` list above the composer (rendered separately)
 * but the footer pill dropped to the staleness fallback. We emit a
 * dedicated `queued` step so the footer pill and the big list stay
 * in sync, and the operator gets the same dot+pulse chrome for the
 * parked state they got for the running state.
 */
export function resolveComposerLiveStep(
  input: ComposerLiveStepInput,
): {
  step: LiveRunStep | null;
  nextStickyLiveRunHint: string | null;
  nextStickyThinkingSummary: string | null;
} {
  const queuedCount = Math.max(0, Math.floor(input.queuedCount ?? 0));
  if (!input.runActive) {
    if (queuedCount > 0) {
      return {
        step: {
          id: "queued",
          label: `Queued · ${queuedCount}`,
          state: "active",
        },
        nextStickyLiveRunHint: input.stickyLiveRunHint ?? null,
        nextStickyThinkingSummary: input.stickyThinkingSummary ?? null,
      };
    }
    return {
      step: null,
      nextStickyLiveRunHint: input.stickyLiveRunHint ?? null,
      nextStickyThinkingSummary: input.stickyThinkingSummary ?? null,
    };
  }
  const { steps, nextStickyLiveRunHint, nextStickyThinkingSummary } =
    buildLiveRunStepsWithSticky({
      toolMessageCount: input.toolMessageCount,
      hasActiveTool: input.hasActiveTool,
      runActive: input.runActive,
      streamText: input.streamText,
      reasoningStreamText: input.reasoningStreamText,
      compactionStatus: input.compactionStatus,
      subagents: input.subagents,
      liveRunHint: input.liveRunHint,
      stickyLiveRunHint: input.stickyLiveRunHint,
      activeFileToolPath: input.activeFileToolPath,
      thinkingSummary: input.thinkingSummary,
      stickyThinkingSummary: input.stickyThinkingSummary,
    });
  return {
    step: pickTopmostLiveStep(steps),
    nextStickyLiveRunHint,
    nextStickyThinkingSummary,
  };
}

export function renderComposerLiveStep(
  input: ComposerLiveStepInput,
): TemplateResult | typeof nothing {
  const { step } = resolveComposerLiveStep(input);
  if (!step) {
    return nothing;
  }
  const variant = stepVariant(step.id);
  const className = `chat-compose-live-pill chat-compose-live-pill--${variant}`;
  return html`
    <span
      class=${className}
      role="status"
      aria-live="polite"
      data-step-id=${step.id}
      data-step-variant=${variant}
      data-testid="composer-live-step"
    >
      <span class="chat-compose-live-pill__dot" aria-hidden="true"></span>
      <span class="chat-compose-live-pill__label">${step.label}</span>
    </span>
  `;
}
